export type BusinessType = "school_campus" | "supermarket" | "fashion" | "ready_to_wear" | "food" | "salon" | "tailor" | "pharmacy" | "gadget_repair" | "building" | "laundry" | "fabric_textile";

export interface BusinessTypeConfig {
  label: string;
  shortLabel: string;
  emoji: string;
  tagline: string;
  description: string;
  accentClass: string;
  bgClass: string;
  textClass: string;
  borderClass: string;
  lightBgClass: string;
  badgeClass: string;
  modules: {
    inventory: boolean;
    purchases: boolean;
    suppliers: boolean;
    customers: boolean;
    loyalty: boolean;
    accounting: boolean;
    returns: boolean;
    attendance: boolean;
    checklists: boolean;
    giftRegistry: boolean;
  };
  quickActions: Array<{ label: string; href: string }>;
  invoicePrefix: string;
  demoEmail: string;
  demoPassword: string;
  inventoryTypes?: string[];
  specialFeatures?: string;
  unitModes?: string[];
}

export const BUSINESS_TYPES: Record<BusinessType, BusinessTypeConfig> = {
  school_campus: {
    label: "School & Campus",
    shortLabel: "School & Campus",
    emoji: "🎓",
    tagline: "Campus shops and student fees, organized by term",
    description: "Manage bookshop, stationery, uniform, and campus-supply sales alongside student charges and balances by academic term.",
    accentClass: "blue",
    bgClass: "bg-blue-500",
    textClass: "text-blue-600",
    borderClass: "border-blue-200",
    lightBgClass: "bg-blue-50",
    badgeClass: "bg-blue-100 text-blue-700 border-blue-200",
    modules: {
      inventory: true, purchases: true, suppliers: true,
      customers: true, loyalty: false, accounting: true,
      returns: true, attendance: true, checklists: false, giftRegistry: false,
    },
    quickActions: [
      { label: "New Sale", href: "/sales/new" },
      { label: "Add Product", href: "/inventory?add=1" },
      { label: "Purchase Order", href: "/purchases?add=1" },
      { label: "P&L Report", href: "/accounting/profit-loss" },
    ],
    invoicePrefix: "SCH",
    demoEmail: "demo@school.retailpulsepos.com",
    demoPassword: "Demo1234!",
    inventoryTypes: ["Books & Textbooks", "Stationery", "School Uniforms", "Bags & Backpacks", "Learning Materials"],
  },
  supermarket: {
    label: "Supermarket / Grocery",
    shortLabel: "Supermarket & Grocery",
    emoji: "🛒",
    tagline: "Retail & wholesale grocery, supermarket & provision shop",
    description: "Manage high-volume stock, multiple POS terminals, and supplier relationships with ease.",
    accentClass: "emerald",
    bgClass: "bg-emerald-500",
    textClass: "text-emerald-600",
    borderClass: "border-emerald-200",
    lightBgClass: "bg-emerald-50",
    badgeClass: "bg-emerald-100 text-emerald-700 border-emerald-200",
    modules: {
      inventory: true, purchases: true, suppliers: true,
      customers: true, loyalty: true, accounting: true,
      returns: true, attendance: true, checklists: true, giftRegistry: false,
    },
    quickActions: [
      { label: "New Sale", href: "/sales/new" },
      { label: "Add Product", href: "/inventory?add=1" },
      { label: "Purchase Order", href: "/purchases?add=1" },
      { label: "P&L Report", href: "/accounting/profit-loss" },
    ],
    invoicePrefix: "GRC",
    demoEmail: "demo@supermarket.retailpulsepos.com",
    demoPassword: "Demo1234!",
  },
  fashion: {
    label: "Fashion & Clothing",
    shortLabel: "Fashion & Boutique",
    emoji: "👗",
    tagline: "Boutiques, kiddies stores, adult & children's clothing",
    description: "Perfect for clothing boutiques, kiddies stores (like Carters), adult fashion (like Zara), and any apparel retailer. Track sizes, styles, returns, and loyal customers.",
    accentClass: "purple",
    bgClass: "bg-purple-500",
    textClass: "text-purple-600",
    borderClass: "border-purple-200",
    lightBgClass: "bg-purple-50",
    badgeClass: "bg-purple-100 text-purple-700 border-purple-200",
    modules: {
      inventory: true, purchases: true, suppliers: true,
      customers: true, loyalty: true, accounting: true,
      returns: true, attendance: true, checklists: false, giftRegistry: true,
    },
    quickActions: [
      { label: "New Sale", href: "/sales/new" },
      { label: "Add Style", href: "/inventory?add=1" },
      { label: "Gift Registry", href: "/gift-registry" },
      { label: "Returns", href: "/returns" },
    ],
    invoicePrefix: "FBC",
    demoEmail: "demo@fashion.retailpulsepos.com",
    demoPassword: "Demo1234!",
  },
  ready_to_wear: {
    label: "Open Market / Clothing Trader",
    shortLabel: "Ready-to-Wear",
    emoji: "👔",
    tagline: "Market stalls, bale traders & open-market clothing sellers",
    description: "Built for open-market cloth traders, bale sellers, and market stall owners. Quick sales, stock tracking, and daily cash management — no loyalty programmes needed.",
    accentClass: "orange",
    bgClass: "bg-orange-500",
    textClass: "text-orange-600",
    borderClass: "border-orange-200",
    lightBgClass: "bg-orange-50",
    badgeClass: "bg-orange-100 text-orange-700 border-orange-200",
    modules: {
      inventory: true, purchases: true, suppliers: true,
      customers: false, loyalty: false, accounting: true,
      returns: true, attendance: true, checklists: false, giftRegistry: false,
    },
    quickActions: [
      { label: "New Sale", href: "/sales/new" },
      { label: "Inventory", href: "/inventory" },
      { label: "Returns", href: "/returns" },
      { label: "Expenses", href: "/expenses" },
    ],
    invoicePrefix: "RTW",
    demoEmail: "demo@readytowear.retailpulsepos.com",
    demoPassword: "Demo1234!",
  },
  food: {
    label: "Food, Buka & Fresh Mart",
    shortLabel: "Food, Buka & Fresh Mart",
    emoji: "🍽️",
    tagline: "Cooked meals, frozen proteins, bulk grain unit breakdowns, and daily market logs",
    description: "Manage menu orders, frozen food stock, bulk grain sales, and daily market logs — with unit conversion tracking from bags to DeRica and generator fuel expense logging.",
    accentClass: "red",
    bgClass: "bg-red-500",
    textClass: "text-red-600",
    borderClass: "border-red-200",
    lightBgClass: "bg-red-50",
    badgeClass: "bg-red-100 text-red-700 border-red-200",
    modules: {
      inventory: true, purchases: true, suppliers: true,
      customers: true, loyalty: false, accounting: true,
      returns: false, attendance: true, checklists: true, giftRegistry: false,
    },
    quickActions: [
      { label: "New Order", href: "/sales/new" },
      { label: "Daily Sales", href: "/sales" },
      { label: "Expenses", href: "/expenses" },
      { label: "Staff Rota", href: "/attendance" },
    ],
    invoicePrefix: "FDK",
    demoEmail: "demo@food.retailpulsepos.com",
    demoPassword: "Demo1234!",
    inventoryTypes: ["Cooked Meals", "Frozen Food", "Bulk Grains"],
    specialFeatures: "Unit conversion tracking (e.g., Bag to DeRica) and generator fuel logs",
    unitModes: ["Per Plate", "Per KG", "Per DeRica/Paint Bucket"],
  },
  salon: {
    label: "Salon & Spa",
    shortLabel: "Salon & Beauty",
    emoji: "✂️",
    tagline: "Appointments, spa services, staff schedules & beauty catalog",
    description: "Schedule client appointments, manage salon and spa services, track staff performance, and grow your clientele.",
    accentClass: "pink",
    bgClass: "bg-pink-500",
    textClass: "text-pink-600",
    borderClass: "border-pink-200",
    lightBgClass: "bg-pink-50",
    badgeClass: "bg-pink-100 text-pink-700 border-pink-200",
    modules: {
      inventory: false, purchases: false, suppliers: false,
      customers: true, loyalty: true, accounting: true,
      returns: false, attendance: true, checklists: true, giftRegistry: false,
    },
    quickActions: [
      { label: "New Booking", href: "/sales/new" },
      { label: "Clients", href: "/customers" },
      { label: "Staff Schedule", href: "/attendance" },
      { label: "Revenue", href: "/reports" },
    ],
    invoicePrefix: "SLN",
    demoEmail: "demo@salon.retailpulsepos.com",
    demoPassword: "Demo1234!",
  },
  tailor: {
    label: "Fashion Designer",
    shortLabel: "Tailor & Bespoke",
    emoji: "🧵",
    tagline: "Custom orders, fabric tracking & client measurements",
    description: "For fashion designers, tailors, and seamstresses who sew and sell. Take custom orders, track fabric & materials, manage client measurements, and invoice completed pieces.",
    accentClass: "teal",
    bgClass: "bg-teal-500",
    textClass: "text-teal-600",
    borderClass: "border-teal-200",
    lightBgClass: "bg-teal-50",
    badgeClass: "bg-teal-100 text-teal-700 border-teal-200",
    modules: {
      inventory: true, purchases: true, suppliers: true,
      customers: true, loyalty: true, accounting: true,
      returns: false, attendance: true, checklists: true, giftRegistry: true,
    },
    quickActions: [
      { label: "New Order", href: "/sales/new" },
      { label: "Clients", href: "/customers" },
      { label: "Fabric Stock", href: "/inventory" },
      { label: "Expenses", href: "/expenses" },
    ],
    invoicePrefix: "TLR",
    demoEmail: "demo@tailor.retailpulsepos.com",
    demoPassword: "Demo1234!",
  },
  pharmacy: {
    label: "Pharmacy",
    shortLabel: "Pharmacy",
    emoji: "💊",
    tagline: "Dispensary, medicine retail & healthcare products",
    description: "Track medicines and healthcare products, manage expiry dates, handle prescriptions, and control stock levels across your pharmacy.",
    accentClass: "teal",
    bgClass: "bg-teal-500",
    textClass: "text-teal-600",
    borderClass: "border-teal-200",
    lightBgClass: "bg-teal-50",
    badgeClass: "bg-teal-100 text-teal-700 border-teal-200",
    modules: {
      inventory: true, purchases: true, suppliers: true,
      customers: true, loyalty: false, accounting: true,
      returns: true, attendance: true, checklists: true, giftRegistry: false,
    },
    quickActions: [
      { label: "New Sale", href: "/sales/new" },
      { label: "Stock", href: "/inventory" },
      { label: "Purchases", href: "/purchases" },
      { label: "Expenses", href: "/expenses" },
    ],
    invoicePrefix: "PHA",
    demoEmail: "demo@pharmacy.retailpulsepos.com",
    demoPassword: "Demo1234!",
  },
  gadget_repair: {
    label: "Gadgets & Electronics Repair",
    shortLabel: "Gadgets & Repair",
    emoji: "📱",
    tagline: "Gadget sales, device repair & electronics retail",
    description: "Manage device repairs, track serial and IMEI numbers, log technician work orders, and sell electronics and accessories.",
    accentClass: "blue",
    bgClass: "bg-blue-500",
    textClass: "text-blue-600",
    borderClass: "border-blue-200",
    lightBgClass: "bg-blue-50",
    badgeClass: "bg-blue-100 text-blue-700 border-blue-200",
    modules: {
      inventory: true, purchases: true, suppliers: true,
      customers: true, loyalty: false, accounting: true,
      returns: true, attendance: true, checklists: false, giftRegistry: false,
    },
    quickActions: [
      { label: "New Sale", href: "/sales/new" },
      { label: "Inventory", href: "/inventory" },
      { label: "Repairs", href: "/sales" },
      { label: "Expenses", href: "/expenses" },
    ],
    invoicePrefix: "GDG",
    demoEmail: "demo@gadgets.retailpulsepos.com",
    demoPassword: "Demo1234!",
  },
  building: {
    label: "Building Materials",
    shortLabel: "Building Materials",
    emoji: "🏗️",
    tagline: "Hardware, construction materials & wholesale supplies",
    description: "Handle bulk stock, waybill logistics, wholesale pricing, and supplier relationships for building and construction materials.",
    accentClass: "amber",
    bgClass: "bg-amber-500",
    textClass: "text-amber-600",
    borderClass: "border-amber-200",
    lightBgClass: "bg-amber-50",
    badgeClass: "bg-amber-100 text-amber-700 border-amber-200",
    modules: {
      inventory: true, purchases: true, suppliers: true,
      customers: true, loyalty: false, accounting: true,
      returns: true, attendance: true, checklists: false, giftRegistry: false,
    },
    quickActions: [
      { label: "New Sale", href: "/sales/new" },
      { label: "Stock", href: "/inventory" },
      { label: "Suppliers", href: "/suppliers" },
      { label: "P&L Report", href: "/accounting/profit-loss" },
    ],
    invoicePrefix: "BLD",
    demoEmail: "demo@building.retailpulsepos.com",
    demoPassword: "Demo1234!",
  },
  fabric_textile: {
    label: "Fabric & Textile",
    shortLabel: "Fabric & Textile",
    emoji: "🪢",
    tagline: "Ankara, Lace, George & textile rolls by the yard",
    description: "For fabric merchants selling Ankara, Lace, George, Aso-Oke, Chiffon and more. Track rolls by yard, manage fabric lines, and sell by the metre to tailors and individual buyers.",
    accentClass: "purple",
    bgClass: "bg-purple-500",
    textClass: "text-purple-600",
    borderClass: "border-purple-200",
    lightBgClass: "bg-purple-50",
    badgeClass: "bg-purple-100 text-purple-700 border-purple-200",
    modules: {
      inventory: true, purchases: true, suppliers: true,
      customers: true, loyalty: false, accounting: true,
      returns: true, attendance: true, checklists: false, giftRegistry: false,
    },
    quickActions: [
      { label: "Sell by Yard", href: "/sales/new" },
      { label: "Fabric Stock", href: "/inventory" },
      { label: "Suppliers",    href: "/suppliers" },
      { label: "Expenses",     href: "/expenses" },
    ],
    invoicePrefix: "FBR",
    demoEmail: "demo@fabric.retailpulsepos.com",
    demoPassword: "Demo1234!",
  },
  laundry: {
    label: "Laundry & Dry Cleaning",
    shortLabel: "Laundry & Dry Cleaning",
    emoji: "🫧",
    tagline: "Garment care, laundry orders & pickup tracking",
    description: "Tag garments, track fabric care stages, manage customer pickup and delivery schedules, and handle laundry orders with ease.",
    accentClass: "sky",
    bgClass: "bg-sky-500",
    textClass: "text-sky-600",
    borderClass: "border-sky-200",
    lightBgClass: "bg-sky-50",
    badgeClass: "bg-sky-100 text-sky-700 border-sky-200",
    modules: {
      inventory: true, purchases: false, suppliers: false,
      customers: true, loyalty: true, accounting: true,
      returns: false, attendance: true, checklists: true, giftRegistry: false,
    },
    quickActions: [
      { label: "New Order", href: "/sales/new" },
      { label: "Customers", href: "/customers" },
      { label: "Expenses", href: "/expenses" },
      { label: "Reports", href: "/reports" },
    ],
    invoicePrefix: "LDY",
    demoEmail: "demo@laundry.retailpulsepos.com",
    demoPassword: "Demo1234!",
  },
};

export const BUSINESS_TYPE_LIST = (Object.entries(BUSINESS_TYPES) as [BusinessType, BusinessTypeConfig][]).map(
  ([type, config]) => ({ type, ...config })
);
